/**
 * Landing template strip: real screenshots of the ClarCV HTML templates.
 *
 * Images are generated by the backend management command
 * `generate_template_thumbnails`, so what visitors see here is the
 * actual output, not a mockup.
 *
 * The strip auto-advances in an endless loop (the list is rendered twice and
 * the scroll position wraps at the seam) and pauses whenever the visitor
 * hovers, drags or uses the arrows. On touch, vertical pans scroll the page;
 * horizontal pans drag the strip. Native overflow scrolling is not used,
 * because it fights the autoplay loop.
 */

import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { REWRITE } from '../utils/paths';
import { getTemplateDisplayName } from '../utils/templateNames';
import './LandingTemplates.css';

/** All 18 templates, interleaved by category so the strip never looks repetitive. */
const TEMPLATES = [
  { slug: 'silicon_valley_startup', name: 'Silicon Valley', category: 'modern' },
  { slug: 'elegant_classic_sophisticated', name: 'Elegant Classic', category: 'classic' },
  { slug: 'nordic_minimal_clean', name: 'Nordic Minimal', category: 'minimal' },
  { slug: 'creative_modernist_bold', name: 'Creative Modernist', category: 'creative' },
  { slug: 'premium_dark_modern', name: 'Premium Dark', category: 'modern' },
  { slug: 'oxford_academic_classic', name: 'Oxford Academic', category: 'classic' },
  { slug: 'swiss_design_system', name: 'Swiss Design', category: 'creative' },
  { slug: 'paper_white_minimal', name: 'Paper White', category: 'minimal' },
  { slug: 'tokyo_night_cyber', name: 'Tokyo Night', category: 'modern' },
  { slug: 'parisian_chic_elegant', name: 'Parisian Chic', category: 'classic' },
  { slug: 'bold_statement_creative', name: 'Bold Statement', category: 'creative' },
  { slug: 'minimalist_luxury_elegant', name: 'Minimalist Luxury', category: 'minimal' },
  { slug: 'futuristic_tech_ultra', name: 'Futuristic Tech', category: 'modern' },
  { slug: 'luxury_executive_premium', name: 'Luxury Executive', category: 'classic' },
  { slug: 'portfolio_artist_creative', name: 'Portfolio Artist', category: 'creative' },
  { slug: 'mono_space_minimal', name: 'Mono Space', category: 'minimal' },
  { slug: 'gradient_flow_modern', name: 'Gradient Flow', category: 'modern' },
  { slug: 'art_deco_luxe', name: 'Art Deco Luxe', category: 'classic' },
];

const CATEGORY_KEYS = {
  modern: 'landing.templatesCatModern',
  classic: 'landing.templatesCatClassic',
  creative: 'landing.templatesCatCreative',
  minimal: 'landing.templatesCatMinimal',
};

/** Upload first: templates in the studio are filled with their CV, not empty. */
const TEMPLATES_CTA = {
  pathname: REWRITE,
  search: '?from=templates',
};

/** Pointer travel before a press turns into a drag instead of a click. */
const DRAG_THRESHOLD = 8;

/** Idle drift, in pixels per second. Slow enough to read every name. */
const AUTO_SPEED = 26;

/** Fraction of the pending distance covered per frame after an arrow or swipe. */
const GLIDE_EASING = 0.14;

/** Fallback pixel height for wheels that report deltas in lines. */
const WHEEL_LINE_HEIGHT = 40;

/** How many thumbnails load upfront; the rest stream in as they approach. */
const EAGER_IMAGES = 2;

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const LandingTemplates = () => {
  const { t, lang } = useLanguage();
  const rowRef = useRef(null);
  const nudgeRef = useRef(() => {});

  useEffect(() => {
    const row = rowRef.current;
    if (!row) {
      return undefined;
    }

    const viewport = row.parentElement;
    const reduceMotion = prefersReducedMotion();
    const canHover =
      typeof window !== 'undefined' && window.matchMedia('(hover: hover)').matches;
    const state = { paused: false, pending: 0, loopWidth: 0, offset: 0 };
    const drag = {
      active: false,
      moved: false,
      axis: null,
      startX: 0,
      startY: 0,
      origin: 0,
      pointerId: null,
    };
    let frame = null;
    let lastTime = 0;

    // The exact seam is where the duplicated list begins, so measure it from
    // the DOM instead of halving scrollWidth (gaps would skew that).
    const measure = () => {
      const cards = row.querySelectorAll('.landing-template');
      state.loopWidth =
        cards.length > TEMPLATES.length
          ? cards[TEMPLATES.length].offsetLeft - cards[0].offsetLeft
          : 0;
    };

    const remeasure = () => {
      measure();
      moveTo(state.offset);
    };

    // Track the offset ourselves as a float. Reading `scrollLeft` back each
    // frame loses the fraction, and at this speed every frame moves less than
    // one pixel, so a read-modify-write would round the motion away to zero.
    const moveTo = (value) => {
      let next = value;
      if (state.loopWidth > 0) {
        next = ((next % state.loopWidth) + state.loopWidth) % state.loopWidth;
      }
      state.offset = next;
      row.scrollLeft = next;
    };

    const tick = (now) => {
      const elapsed = Math.min(now - lastTime, 64) / 1000;
      lastTime = now;

      if (!drag.active) {
        let delta = 0;
        if (state.pending) {
          const step = state.pending * GLIDE_EASING;
          state.pending -= step;
          delta = step;
          if (Math.abs(state.pending) < 0.5) {
            state.pending = 0;
          }
        } else if (!state.paused && !reduceMotion) {
          delta = AUTO_SPEED * elapsed;
        }
        if (delta) {
          moveTo(state.offset + delta);
        }
      }

      frame = requestAnimationFrame(tick);
    };

    const start = () => {
      if (frame === null) {
        lastTime = performance.now();
        frame = requestAnimationFrame(tick);
      }
    };

    const stop = () => {
      if (frame !== null) {
        cancelAnimationFrame(frame);
        frame = null;
      }
    };

    nudgeRef.current = (direction) => {
      const card = row.querySelector('.landing-template');
      const step = card ? (card.offsetWidth + 16) * 2 : row.clientWidth * 0.7;
      state.pending += direction * step;
    };

    // Vertical wheel keeps scrolling the page: hijacking it inside an endless
    // strip would trap the visitor. Horizontal gestures move the strip.
    const handleWheel = (event) => {
      if (event.ctrlKey || Math.abs(event.deltaX) <= Math.abs(event.deltaY)) {
        return;
      }
      event.preventDefault();
      const delta = event.deltaMode === 1 ? event.deltaX * WHEEL_LINE_HEIGHT : event.deltaX;
      state.pending += delta;
    };

    const handlePointerDown = (event) => {
      if (event.button !== 0) {
        return;
      }
      // Sync in case a native swipe moved the strip while JS was paused.
      state.offset = row.scrollLeft;
      state.pending = 0;
      drag.active = true;
      drag.moved = false;
      drag.axis = null;
      drag.startX = event.clientX;
      drag.startY = event.clientY;
      drag.origin = state.offset;
      drag.pointerId = event.pointerId;
    };

    const handlePointerMove = (event) => {
      if (!drag.active) {
        return;
      }
      const dx = event.clientX - drag.startX;
      const dy = event.clientY - drag.startY;

      if (!drag.axis) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) < DRAG_THRESHOLD) {
          return;
        }
        // Vertical intent belongs to the page. Do not capture the pointer or
        // the browser will steal the scroll the visitor just started.
        drag.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        if (drag.axis === 'y') {
          return;
        }
        drag.moved = true;
        row.setPointerCapture(drag.pointerId);
        row.classList.add('is-dragging');
      }

      if (drag.axis !== 'x') {
        return;
      }

      event.preventDefault();
      moveTo(drag.origin - dx);
    };

    const endDrag = () => {
      if (!drag.active) {
        return;
      }
      if (drag.moved) {
        if (row.hasPointerCapture(drag.pointerId)) {
          row.releasePointerCapture(drag.pointerId);
        }
        row.classList.remove('is-dragging');
      }
      drag.active = false;
      drag.axis = null;
    };

    const pause = () => {
      state.paused = true;
    };

    const resume = () => {
      state.paused = false;
    };

    const handleVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        start();
      }
    };

    measure();
    start();

    // Card widths settle once fonts and thumbnails land; re-read the seam then.
    const observer = new ResizeObserver(remeasure);
    observer.observe(row);

    window.addEventListener('resize', remeasure);
    document.addEventListener('visibilitychange', handleVisibility);
    viewport.addEventListener('wheel', handleWheel, { passive: false });
    if (canHover) {
      viewport.addEventListener('pointerenter', pause);
      viewport.addEventListener('pointerleave', resume);
    }
    viewport.addEventListener('focusin', pause);
    viewport.addEventListener('focusout', resume);
    row.addEventListener('pointerdown', handlePointerDown);
    row.addEventListener('pointermove', handlePointerMove, { passive: false });
    window.addEventListener('pointerup', endDrag);
    window.addEventListener('pointercancel', endDrag);

    return () => {
      stop();
      observer.disconnect();
      window.removeEventListener('resize', remeasure);
      document.removeEventListener('visibilitychange', handleVisibility);
      viewport.removeEventListener('wheel', handleWheel);
      if (canHover) {
        viewport.removeEventListener('pointerenter', pause);
        viewport.removeEventListener('pointerleave', resume);
      }
      viewport.removeEventListener('focusin', pause);
      viewport.removeEventListener('focusout', resume);
      row.removeEventListener('pointerdown', handlePointerDown);
      row.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', endDrag);
      window.removeEventListener('pointercancel', endDrag);
    };
  }, []);

  const renderCard = (template, index, duplicated) => {
    const displayName = getTemplateDisplayName(template.name, lang);
    return (
      <li
        key={duplicated ? `${template.slug}-clone` : template.slug}
        className="landing-template"
        aria-hidden={duplicated ? 'true' : undefined}
      >
        <img
          className="landing-template__shot"
          src={`/templates/${template.slug}.webp`}
          alt={duplicated ? '' : t('landing.templatesAlt', { name: displayName })}
          width="620"
          height="877"
          loading={!duplicated && index < EAGER_IMAGES ? 'eager' : 'lazy'}
          decoding="async"
          draggable="false"
        />
        <div className="landing-template__meta">
          <span className="landing-template__name">{displayName}</span>
          <span className="landing-template__chip">{t(CATEGORY_KEYS[template.category])}</span>
        </div>
      </li>
    );
  };

  return (
    <section className="landing-templates" aria-labelledby="landing-templates-title">
      <div className="container landing-templates__head">
        <h2 className="landing-templates__title" id="landing-templates-title">
          {t('landing.templatesTitle')}
        </h2>
        <p className="landing-templates__lead">{t('landing.templatesLead')}</p>
      </div>

      <div className="landing-templates__viewport">
        <button
          type="button"
          className="landing-templates__arrow landing-templates__arrow--prev"
          onClick={() => nudgeRef.current(-1)}
          aria-label={t('landing.templatesPrev')}
        >
          <span aria-hidden="true">‹</span>
        </button>

        <ul className="landing-templates__row" ref={rowRef}>
          {TEMPLATES.map((template, index) => renderCard(template, index, false))}
          {TEMPLATES.map((template, index) => renderCard(template, index, true))}
        </ul>

        <button
          type="button"
          className="landing-templates__arrow landing-templates__arrow--next"
          onClick={() => nudgeRef.current(1)}
          aria-label={t('landing.templatesNext')}
        >
          <span aria-hidden="true">›</span>
        </button>
      </div>

      <div className="container landing-templates__foot">
        <Link to={TEMPLATES_CTA} className="btn btn-primary">
          {t('landing.templatesCta')}
        </Link>
        <p className="landing-templates__note">{t('landing.templatesNote')}</p>
      </div>
    </section>
  );
};

export default LandingTemplates;
